import React, {useEffect, useRef, useState} from 'react';
import {PRISMAX_ADMIN_DOWNLOADS_URL} from '../../configs/config';
import {downloadSignedFile, downloadSignedFileToDirectory, supportsDirectoryDownloads} from './browserDownloads';
import AdminDownloadVerificationDialog from './AdminDownloadVerificationDialog';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import styles from './AdminBrowserDownloads.module.css';
import panelStyles from './RoboticDataWorkspace.module.css';
import {PanelsTopLeft, History, Download} from 'lucide-react';

export const ADMIN_DOWNLOAD_MAX_EPISODES = 30;

export default function AdminBrowserDownloads({adminToken, episodeIds, selectedEpisodes = [], onClear, onRemove, onBusyChange, onSessionExpired}) {
    const [busy, setBusy] = useState(false);
    const [verificationSelection, setVerificationSelection] = useState(null);
    const locked = busy || Boolean(verificationSelection);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [nextBeforeId, setNextBeforeId] = useState(null);
    const controllerRef = useRef(null);
    const historyControllerRef = useRef(null);
    const busyRef = useRef(false);
    // Chrome/Edge: one subfolder per episode inside a folder the admin picks. Other browsers: flat file downloads.
    const directoryHandleRef = useRef(null);

    useEffect(() => () => {
        controllerRef.current?.abort();
        historyControllerRef.current?.abort();
    }, []);

    const reportError = (err, setter) => {
        if (err.name === 'AbortError') return;
        if (isSessionExpiredError(err.message)) onSessionExpired?.();
        setter(err.message || 'Request failed.');
    };

    const loadHistory = async (beforeId = null) => {
        historyControllerRef.current?.abort();
        const controller = new AbortController();
        historyControllerRef.current = controller;
        setHistoryLoading(true);
        setHistoryError('');
        try {
            const response = await fetch(`${PRISMAX_ADMIN_DOWNLOADS_URL}/data/admin/downloads/history${beforeId ? `?before_id=${beforeId}` : ''}`, {
                headers: {Authorization: `Bearer ${adminToken}`},
                signal: controller.signal,
            });
            const json = await response.json();
            if (!response.ok || !json.success) throw new Error(getApiErrorMessage(json, 'Could not load download history.'));
            if (controller.signal.aborted) return;
            setHistory((current) => beforeId ? [...current, ...json.data] : json.data);
            setNextBeforeId(json.pagination?.next_before_id || null);
        } catch (err) {
            if (!controller.signal.aborted) reportError(err, setHistoryError);
        } finally {
            if (!controller.signal.aborted) setHistoryLoading(false);
        }
    };

    const startDownload = async (data) => {
        if (busyRef.current) return;
        setVerificationSelection(null);
        busyRef.current = true;
        setBusy(true);
        setError('');
        setMessage('Preparing download…');
        const controller = new AbortController();
        controllerRef.current = controller;
        try {
            const files = data?.files || [];
            if (!files.length) throw new Error('No downloadable files were returned.');
            const directoryHandle = directoryHandleRef.current;
            for (let index = 0; index < files.length; index += 1) {
                if (controller.signal.aborted) return;
                if (Date.now() >= Date.parse(data.expires_at)) {
                    throw new Error('Download links expired. Select the remaining episodes and download again.');
                }
                const file = files[index];
                if (directoryHandle) {
                    const pathParts = [String(file.episode_id), ...String(file.file_name || file.download_name).split('/')];
                    setMessage(`Downloading ${index + 1}/${files.length}: ${pathParts.join('/')}`);
                    await downloadSignedFileToDirectory(file.url, directoryHandle, pathParts, controller.signal);
                } else {
                    setMessage(`Downloading ${index + 1}/${files.length}: ${file.download_name}`);
                    await downloadSignedFile(file.url, file.download_name, controller.signal);
                }
            }
            const skippedIds = Array.isArray(data?.skipped_episode_ids) ? data.skipped_episode_ids : [];
            const skippedNote = skippedIds.length ? ` Skipped episodes with no files: ${skippedIds.join(', ')}.` : '';
            if (directoryHandle) {
                const folderCount = new Set(files.map((file) => String(file.episode_id))).size;
                setMessage(`${files.length} files saved to "${directoryHandle.name}" in ${folderCount} episode folder${folderCount === 1 ? '' : 's'}.${skippedNote}`);
            } else {
                setMessage(`${files.length} files sent to your browser. Check your browser downloads to confirm they were saved.${skippedNote}`);
            }
        } catch (err) {
            if (!controller.signal.aborted) {
                setMessage('');
                reportError(err, setError);
            }
        } finally {
            busyRef.current = false;
            if (!controller.signal.aborted) {
                setBusy(false);
                onBusyChange(false);
                if (historyOpen) void loadHistory();
            }
        }
    };

    return (
        <section className={styles.panel} aria-label="Administrator browser downloads">
            <div className={panelStyles.rightTop}>
                <div className={panelStyles.rLabel}>Selected episodes</div>
                <button className={styles.clearButton} type="button" onClick={onClear} disabled={locked || !episodeIds.length}>Clear selection</button>
            </div>
            {episodeIds.length > 0 ? <div className={styles.selection}>
                {episodeIds.map((id) => <button key={id} type="button" disabled={locked} onClick={() => onRemove(id)} aria-label={`Remove episode ${id}`}>Ep {id} ×</button>)}
            </div> : <p className={panelStyles.emptyCopy}>Selected episodes will appear here.</p>}
            <hr className={panelStyles.divider}/>
            <div className={panelStyles.statsCard}>
                <div className={panelStyles.statBlock}>
                    <div className={panelStyles.statLabel}>Episodes</div>
                    <div className={panelStyles.statValue} aria-label={`${episodeIds.length}/${ADMIN_DOWNLOAD_MAX_EPISODES} episodes selected`}>
                        {episodeIds.length}<span className={panelStyles.statQuota}> / {ADMIN_DOWNLOAD_MAX_EPISODES}</span>
                    </div>
                </div>
                <div className={panelStyles.statBlock}>
                    <div className={panelStyles.statLabel}>Video files</div>
                    <div className={`${panelStyles.statValue} ${panelStyles.statBlue}`}>
                        {!episodeIds.length ? 0 : selectedEpisodes.length === episodeIds.length && selectedEpisodes.every((episode) => Number.isFinite(episode.video_count))
                            ? selectedEpisodes.reduce((count, episode) => count + episode.video_count, 0) : '—'}
                    </div>
                </div>
                <div className={panelStyles.statBlock}>
                    <div className={panelStyles.statLabel}>MCAP files</div>
                    <div className={`${panelStyles.statValue} ${panelStyles.statAmber}`}>{episodeIds.length}</div>
                </div>
            </div>
            <div className={panelStyles.tabBar}>
                <button type="button" className={`${panelStyles.tabItem} ${!historyOpen ? panelStyles.tabItemActive : ''}`} aria-pressed={!historyOpen} onClick={() => setHistoryOpen(false)}><PanelsTopLeft/><span>Browser</span></button>
                <button type="button" className={`${panelStyles.tabItem} ${historyOpen ? panelStyles.tabItemActive : ''}`} aria-label="My download history" aria-pressed={historyOpen} onClick={() => {
                    setHistoryOpen(true);
                    if (!historyOpen) void loadHistory();
                }}><History/><span>History</span></button>
            </div>
            <div className={panelStyles.tabPanelWrap}>
            {!historyOpen && <>
                <p className={panelStyles.tabNote}>Select up to 30 episodes. Verify by email to download. Links are valid for 30 minutes.</p>
                <button type="button" className={panelStyles.primaryButton} onClick={async () => {
                    setError(''); setMessage('');
                    directoryHandleRef.current = null;
                    if (supportsDirectoryDownloads()) {
                        // Must run inside the click: the browser only opens the picker on a user gesture.
                        try {
                            directoryHandleRef.current = await window.showDirectoryPicker({id: 'prismax-admin-downloads', mode: 'readwrite'});
                        } catch (err) {
                            if (err?.name === 'AbortError') return;
                            directoryHandleRef.current = null;
                        }
                    }
                    setVerificationSelection([...episodeIds]);
                    onBusyChange(true);
                }} disabled={locked || !adminToken || !episodeIds.length || episodeIds.length > ADMIN_DOWNLOAD_MAX_EPISODES}>
                    <Download/>{busy ? 'Downloading…' : 'Browser Download'}
                </button>
                <p className={styles.note}>Allow multiple downloads in your browser.</p>
            </>}
            {verificationSelection && <AdminDownloadVerificationDialog adminToken={adminToken} episodeIds={verificationSelection}
                onClose={() => { setVerificationSelection(null); onBusyChange(false); }}
                onReady={(data) => void startDownload(data)} onSessionExpired={onSessionExpired}/>}
            {message && <p role="status">{message}</p>}
            {error && <p className={styles.error} role="alert">{error}</p>}
            {historyOpen && <div className={styles.history}>
                <div className={styles.toolbar}><strong>My download history</strong><button type="button" disabled={historyLoading} onClick={() => void loadHistory()}>Refresh</button></div>
                <p className={styles.note}>Records show link preparation, not confirmation that files were saved.</p>
                {historyError && <p role="alert" className={styles.error}>{historyError}</p>}
                {historyLoading && <p role="status">Loading history…</p>}
                {!historyLoading && !historyError && !history.length && <p>No download requests yet.</p>}
                {history.map((entry) => <article className={styles.record} key={entry.download_id}>
                    <div>#{entry.download_id} · {new Date(entry.created_at).toLocaleString()} · {{LINKS_READY: 'Links generated', FAILED: 'Preparation failed', PENDING: 'Preparation pending'}[entry.status] || entry.status}</div>
                    <div>{entry.file_count} files · {(Number(entry.total_bytes) / (1024 * 1024)).toFixed(1)} MB</div>
                    {entry.verification_recipient && <div>Email verified: {{jason: 'Ja****', bayley: 'Ba***', chris: 'Ch***'}[entry.verification_recipient]}</div>}
                    <div>Episodes: {entry.selected_episode_ids.join(', ')}</div>
                    {entry.error_message && <div className={styles.error}>{entry.error_message}</div>}
                </article>)}
                {nextBeforeId && <button type="button" disabled={historyLoading} onClick={() => void loadHistory(nextBeforeId)}>Older records</button>}
            </div>}
            </div>
        </section>
    );
}
